import { useApiClient } from '@/composables/useApiClient'
import type { MessageContent } from '../types/interfaces'

type AiTranslate = NonNullable<MessageContent['ai_translate']>

export interface MessageTranslationDelta {
  message_id: string
  ai_translate: AiTranslate
}

export interface ChatTranslationSyncResponse {
  translated: MessageTranslationDelta[]
  skipped: number
  failed: Array<{
    message_id: string
    status: 'error'
  }>
}

export interface ChatTranslationSyncRequest {
  target_language: string
  limit: number
  offset: number
}

export interface ChatTranslationSyncWindowRequest {
  target_language: string
  message_ids: string[]
}

export interface MessageTranslationRequest {
  target_language: string
}

export interface MessageTranslationPreviewRequest {
  campaign_id: string
  text: string
  target_language: string
}

export interface MessageTranslationPreviewResponse {
  translated_content: string
}

export const useMessageTranslationService = () => {
  const apiClient = useApiClient(true)

  const syncChatTranslations = (chatId: string, payload: ChatTranslationSyncRequest) =>
    apiClient.post<ChatTranslationSyncResponse, ChatTranslationSyncRequest>(
      `/oper/chats/${chatId}/translations/sync`,
      payload,
    )

  const translateMessage = (messageId: string, payload: MessageTranslationRequest) =>
    apiClient.post<MessageTranslationDelta, MessageTranslationRequest>(
      `/oper/messages/${messageId}/translate`,
      payload,
    )

  const syncChatTranslationWindow = (
    chatId: string,
    payload: ChatTranslationSyncWindowRequest,
  ) =>
    apiClient.post<ChatTranslationSyncResponse, ChatTranslationSyncWindowRequest>(
      `/oper/chats/${chatId}/translations/sync-window`,
      payload,
    )

  const previewTranslation = (payload: MessageTranslationPreviewRequest) =>
    apiClient.post<MessageTranslationPreviewResponse, MessageTranslationPreviewRequest>(
      '/oper/messages/translation-preview',
      payload,
    )

  return {
    syncChatTranslations,
    syncChatTranslationWindow,
    translateMessage,
    previewTranslation,
  }
}
